import { inject } from '@angular/core';
import type { HttpInterceptorFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, finalize, shareReplay, map, tap } from 'rxjs/operators';
import { Auth } from '@/app/core/services/auth';
import { cleanupParams } from '@/app/core/helpers/query-params';

let refreshToken$: Observable<string> | null = null;

const noTokenUrls = [
  '/api/v1/auth/login',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (noTokenUrls.some((url) => req.url.includes(url)) || req.url.includes('/api/v1/auth/refresh-token')) {
    return next(req);
  }

  const router = inject(Router);
  const auth = inject(Auth);

  const accessToken = auth.authToken()?.accessToken;

  const handle401Error = (originalError: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
    const authToken = auth.authToken();

    if (!authToken) {
      auth.logout();
      return throwError(() => originalError);
    }

    if (!refreshToken$) {
      const request = cleanupParams({
        refreshToken: authToken.refreshToken,
      });

      refreshToken$ = auth.refreshToken(request).pipe(
        map((res) => res.data.accessToken),
        tap({
          error: () => {
            auth.logout();
          },
        }),
        shareReplay(1),
        finalize(() => {
          refreshToken$ = null;
        }),
      );
    }

    return refreshToken$.pipe(
      catchError(() => throwError(() => originalError)),
      switchMap((newToken) => {
        const retryReq = req.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` },
        });
        return next(retryReq);
      }),
    );
  };

  const reqWithToken = accessToken
    ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
    : req;

  return next(reqWithToken).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.includes('/api/v1/auth/logout')) {
        return handle401Error(error);
      }
      return throwError(() => error);
    }),
  );
};
